import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosConfig from '../../utils/axiosConfig';
import { resetProfile, setProfile, setProfiles } from '../profile/profileSlice';
import { initialState } from './authSlice';

const AUTH_API_BASE_URL = '/auth';
const USER_API_BASE_URL = '/user';
export const loginUser = createAsyncThunk('user/login', async ({
	email, password
}, { rejectWithValue, dispatch }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/email/login`, {
			email, 
			password
		});
		dispatch(setProfile(response.data.profile))
		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const logoutUser = createAsyncThunk('user/logout', async (_, { rejectWithValue, dispatch }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/logout`, {}, {isRefresh: true});
		dispatch(resetProfile());
		return response.data.message;

	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const getSession = createAsyncThunk('user/session', async (_, { rejectWithValue, dispatch, getState }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/refresh`, {}, {isRefresh: true});
		if (!response) {
			return initialState;
		}
		dispatch(setProfile(response.data.profile));
		return response.data;

	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const registerUser = createAsyncThunk('user/register', async ({
	firstName, lastName, email, password
}, { rejectWithValue }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/email/register`, {
			firstName: firstName,
			lastName: lastName,
			email: email,
			password
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const forgotPassword = createAsyncThunk('user/forgot/password', async ({
	email
}, { rejectWithValue }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/forgot/password`, {
			email: email
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const resetPassword = createAsyncThunk('auth/reset/password', async ({
	email, code, password
}, { rejectWithValue }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/forgot/password/reset`, {
			email,
			code,
			password
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const verifyForgotOtp = createAsyncThunk('user/forgot/otp/verify', async ({
	email, code
}, { rejectWithValue }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/forgot/otp/verify`, {
			email,
			code
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const confirmUser = createAsyncThunk('user/register/confirm', async ({ code, userId }, { dispatch, rejectWithValue }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/email/confirm`, {
			userId: userId,
			code: code
		});
		dispatch(setProfile(response.data.profile))
		return await response.data.user;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const resendConfirmUser = createAsyncThunk('user/register/confirm/resend', async ({ userId }, { rejectWithValue }) => {
	try {
		const response = await axiosConfig.post(`${AUTH_API_BASE_URL}/email/confirm/resend`);

		return await response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});


export const updateUser = createAsyncThunk('user/update/me', async ({
	firstName, lastName, country, phone, avatarId, password
}, { rejectWithValue }) => {
	try {
		let data = {
			firstName, 
			lastName,
			country,
			phone,
			avatar: avatarId
		}
		if (password && password.trim() !== "") {
			data.password = password;
		}
		
		const response = await axiosConfig.post(`${USER_API_BASE_URL}/me`, data);
		return response.data;
	} catch (error) {
		const msg = error.response?.data?.message;
		return rejectWithValue({
			error: Array.isArray(msg) ? msg.join(', ') : (msg || error.message)
		});
	}
});

const emailChangeError = (error) => {
	const msg = error.response?.data?.message;
	return Array.isArray(msg) ? msg.join(', ') : (msg || error.message);
};

export const requestUserEmailChange = createAsyncThunk(
	'user/email/change/request',
	async ({ email }, { rejectWithValue }) => {
		try {
			const response = await axiosConfig.post(`${USER_API_BASE_URL}/me/email/request`, {
				email: email.toLowerCase().trim(),
			});
			return response.data;
		} catch (error) {
			return rejectWithValue({ error: emailChangeError(error) });
		}
	},
);

export const confirmUserEmailChange = createAsyncThunk(
	'user/email/change/confirm',
	async ({ code }, { rejectWithValue }) => {
		try {
			const response = await axiosConfig.post(`${USER_API_BASE_URL}/me/email/confirm`, {
				code,
			});
			return response.data;
		} catch (error) {
			return rejectWithValue({ error: emailChangeError(error) });
		}
	},
);

export const resendUserEmailChange = createAsyncThunk(
	'user/email/change/resend',
	async (_, { rejectWithValue }) => {
		try {
			const response = await axiosConfig.post(`${USER_API_BASE_URL}/me/email/resend`);
			return response.data;
		} catch (error) {
			return rejectWithValue({ error: emailChangeError(error) });
		}
	},
);

export const uploadAvatar = createAsyncThunk('user/upload/avatar', async ({avatar = null}, { rejectWithValue }) => {

	try {

		if(avatar){
			let formData = new FormData();
			formData.append("avatar", avatar);
			const response = await axiosConfig.post(`${USER_API_BASE_URL}/upload/avatar`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});
			return response.data;
		}
		return;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});
